import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc, collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { Palmtree, Sun, Wind, Cloud, Droplets, MapPin, Bell, Save, Loader, Waves, Smartphone, Crown, Plus, X, AlertCircle } from 'lucide-react';
import PremiumUpgrade from './PremiumUpgrade';
import { createCheckoutSession, createPortalLink } from './StripeCheckout';
import LocationPicker from './LocationPicker';
import WelcomeModal from './WelcomeModal';
import ReferralCard from './ReferralCard';
import { formatTemp, convertTemp, convertTempToF, formatSpeed, convertSpeed, convertSpeedToMph, getTempUnit, getSpeedUnit } from '../utils/unitConversions';

export default function Preferences() {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [preferences, setPreferences] = useState({
    homeLocation: null, // { name, latitude, longitude, displayName, shortName }
    tempMin: 75,
    tempMax: 85,
    maxWindSpeed: 10,
    maxCloudCover: 30,
    maxPrecipChance: 10,
    alertLeadTime: 1,
    alertThreshold: 7, // NEW: Configurable threshold (default 7 out of 10)
    timeOfDayPreference: 'both', // NEW: 'morning', 'afternoon', or 'both'
    emailEnabled: true,
    smsEnabled: false,
    phoneNumber: '',
    wavePreference: 'calm',
    minWaterTemp: 70,
    additionalLocations: [], // Premium: up to 2 more locations (same format as homeLocation)
    isPremium: false,
    units: 'imperial', // NEW: 'imperial' or 'metric'
    scoringPriorities: [], // Premium: Array of up to 3 priorities ['airTemp', 'waterTemp', 'waves', 'wind', 'rain', 'clouds']
    // Legacy support for homeZipCode - will migrate to homeLocation
    homeZipCode: '',
  });

  useEffect(() => {
    if (user) {
      loadPreferences();
      listenToSubscription();
    }
  }, [user]);

  const listenToSubscription = () => {
    // Listen for active Stripe subscriptions
    const subscriptionsRef = collection(db, 'customers', user.uid, 'subscriptions');
    const activeQuery = query(subscriptionsRef, where('status', 'in', ['trialing', 'active']));

    console.log('Listening for subscriptions for user:', user.uid);

    onSnapshot(activeQuery, (snapshot) => {
      console.log('Subscription snapshot:', snapshot.docs.length, 'subscriptions found');

      if (snapshot.docs.length > 0) {
        const sub = snapshot.docs[0].data();
        const isTrialing = sub.status === 'trialing';

        console.log('Active subscription found:', sub);

        // Update user preferences with premium status
        setDoc(doc(db, 'users', user.uid), {
          preferences: {
            ...preferences,
            isPremium: true,
            premiumType: isTrialing ? 'trial' : 'paid',
            subscriptionId: snapshot.docs[0].id,
          },
          updatedAt: new Date().toISOString(),
        }, { merge: true });

        // Update local state
        setPreferences(prev => ({
          ...prev,
          isPremium: true,
          premiumType: isTrialing ? 'trial' : 'paid',
        }));
      } else {
        console.log('No active subscriptions found for user:', user.uid);
      }
    });
  };

  const loadPreferences = async () => {
    try {
      const docRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setPreferences({ ...preferences, ...docSnap.data().preferences });
      } else {
        // First time user - show welcome modal
        setShowWelcome(true);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);

    try {
      // Format phone number with +1 for US
      const formattedPreferences = { ...preferences };
      if (formattedPreferences.phoneNumber && formattedPreferences.phoneNumber.trim()) {
        // Remove any existing +1, spaces, and special characters
        const digitsOnly = formattedPreferences.phoneNumber.replace(/^\+1|\D/g, '');
        // Add +1 prefix
        formattedPreferences.phoneNumber = `+1${digitsOnly}`;
      }

      // Check if this is the first save (user document doesn't exist yet)
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const isFirstSave = !userDocSnap.exists();

      await setDoc(userDocRef, {
        email: user.email,
        displayName: user.displayName,
        preferences: formattedPreferences,
        updatedAt: new Date().toISOString(),
        ...(isFirstSave && { createdAt: new Date().toISOString() })
      }, { merge: true });

      // Process referral if this is first save and there's a ref code in URL
      if (isFirstSave) {
        const urlParams = new URLSearchParams(window.location.search);
        const refCode = urlParams.get('ref');

        if (refCode) {
          try {
            const response = await fetch('https://us-central1-outtaten.cloudfunctions.net/processReferral', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                newUserId: user.uid,
                referralCode: refCode
              })
            });

            const result = await response.json();
            if (result.success) {
              console.log('🎉 Referral processed successfully!');
              // Reload preferences to get premium status
              setTimeout(() => window.location.reload(), 1000);
            }
          } catch (error) {
            console.error('Error processing referral:', error);
            // Don't block signup if referral fails
          }
        }
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving preferences:', error);
      alert('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const updatePref = (key, value) => {
    setPreferences({ ...preferences, [key]: value });
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you ABSOLUTELY SURE you want to delete your account? This action cannot be undone. All your data will be permanently deleted.')) {
      return;
    }

    setDeleting(true);

    try {
      const response = await fetch('https://us-central1-outtaten.cloudfunctions.net/deleteUserAccount', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid })
      });

      const result = await response.json();

      if (result.success) {
        alert('Your account has been deleted. You will now be signed out.');
        // Sign out (Auth will be deleted on backend)
        await import('../auth').then(auth => auth.signOut());
        window.location.href = '/';
      } else {
        throw new Error(result.error || 'Failed to delete account');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      alert(`Failed to delete account: ${error.message}`);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleUpgrade = async (planType) => {
    const priceIds = {
      monthly: process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID,
      yearly: process.env.REACT_APP_STRIPE_YEARLY_PRICE_ID,
    };

    const priceId = priceIds[planType];

    // Check if Price ID is configured
    if (!priceId || priceId.includes('placeholder')) {
      console.error('Stripe Price ID not configured:', priceId);
      alert('Payment system is being configured. Please try again in a few minutes or contact support@10outtaten.com');
      return;
    }

    console.log('Starting checkout with price ID:', priceId);

    try {
      const checkoutUrl = await createCheckoutSession(user.uid, priceId);
      console.log('Checkout URL received:', checkoutUrl);
      window.location.assign(checkoutUrl);
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert(`Unable to start checkout: ${error.message}\n\nPlease contact support@10outtaten.com`);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const portalUrl = await createPortalLink(user.uid);
      window.location.assign(portalUrl);
    } catch (error) {
      console.error('Error opening customer portal:', error);

      // Show a more helpful error message
      const errorMessage = error.message.includes('Customer not found')
        ? `We're having trouble finding your subscription details. This can happen if:\n\n` +
          `• Your subscription was recently created and hasn't synced yet (wait 1-2 minutes and try again)\n` +
          `• You were granted VIP access manually\n\n` +
          `Please try again in a moment, or contact support@10outtaten.com for help.`
        : `Unable to open subscription management: ${error.message}\n\nPlease contact support@10outtaten.com`;

      alert(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="w-8 h-8 text-cyan-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {showWelcome && <WelcomeModal onClose={() => setShowWelcome(false)} />}

      {/* Referral Card */}
      <ReferralCard />

      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-cyan-400 via-blue-400 to-blue-500 px-8 py-10 text-white relative overflow-hidden">
          <div className="absolute top-4 right-4 opacity-20">
            <Palmtree className="w-24 h-24" />
          </div>
          <div className="absolute bottom-4 left-4 opacity-10">
            <Sun className="w-32 h-32" />
          </div>
          <div className="relative">
            <h2 className="text-3xl font-bold mb-2">Your Perfect Beach Day</h2>
            <p className="text-blue-50">Customize what makes a 10/10 day for you</p>
          </div>
        </div>

        <div className="p-8 space-y-8">
          {/* Units Toggle */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-gray-700 font-semibold">Units</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => updatePref('units', 'imperial')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    preferences.units === 'imperial'
                      ? 'bg-cyan-500 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  °F / mph / ft
                </button>
                <button
                  onClick={() => updatePref('units', 'metric')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    preferences.units === 'metric'
                      ? 'bg-cyan-500 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  °C / km/h / m
                </button>
              </div>
            </label>
          </div>

          {/* Location */}
          <div>
            <label className="flex items-center gap-2 text-gray-700 font-semibold mb-3">
              <MapPin className="w-5 h-5 text-cyan-500" />
              Home Location
            </label>
            <LocationPicker
              value={preferences.homeLocation}
              onChange={(location) => updatePref('homeLocation', location)}
              placeholder="Enter ZIP or location name (e.g., Ocean City)"
            />
          </div>

          {/* Temperature Range */}
          <div>
            <label className="flex items-center gap-2 text-gray-700 font-semibold mb-3">
              <Sun className="w-5 h-5 text-orange-500" />
              Perfect Temperature
            </label>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Minimum</span>
                  <span className="text-sm font-semibold text-orange-600">{formatTemp(preferences.tempMin, preferences.units)}</span>
                </div>
                <input
                  type="range"
                  min="60"
                  max="95"
                  value={preferences.tempMin}
                  onChange={(e) => updatePref('tempMin', parseInt(e.target.value))}
                  className="w-full h-2 bg-gradient-to-r from-blue-300 to-orange-300 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Maximum</span>
                  <span className="text-sm font-semibold text-orange-600">{formatTemp(preferences.tempMax, preferences.units)}</span>
                </div>
                <input
                  type="range"
                  min="60"
                  max="95"
                  value={preferences.tempMax}
                  onChange={(e) => updatePref('tempMax', parseInt(e.target.value))}
                  className="w-full h-2 bg-gradient-to-r from-orange-300 to-red-400 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>
            </div>
          </div>

          {/* Wind Speed */}
          <div>
            <label className="flex items-center gap-2 text-gray-700 font-semibold mb-3">
              <Wind className="w-5 h-5 text-gray-500" />
              Maximum Wind Speed
            </label>
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-600">Calm breeze</span>
              <span className="text-sm font-semibold text-gray-700">{formatSpeed(preferences.maxWindSpeed, preferences.units)}</span>
            </div>
            <input
              type="range"
              min="5"
              max="25"
              value={preferences.maxWindSpeed}
              onChange={(e) => updatePref('maxWindSpeed', parseInt(e.target.value))}
              className="w-full h-2 bg-gradient-to-r from-green-300 to-gray-400 rounded-lg appearance-none cursor-pointer slider"
            />
          </div>

          {/* Cloud Cover */}
          <div>
            <label className="flex items-center gap-2 text-gray-700 font-semibold mb-3">
              <Cloud className="w-5 h-5 text-gray-400" />
              Maximum Cloud Coverage
            </label>
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-600">Mostly sunny</span>
              <span className="text-sm font-semibold text-gray-700">{preferences.maxCloudCover}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="70"
              step="5"
              value={preferences.maxCloudCover}
              onChange={(e) => updatePref('maxCloudCover', parseInt(e.target.value))}
              className="w-full h-2 bg-gradient-to-r from-yellow-200 to-gray-400 rounded-lg appearance-none cursor-pointer slider"
            />
          </div>

          {/* Precipitation */}
          <div>
            <label className="flex items-center gap-2 text-gray-700 font-semibold mb-3">
              <Droplets className="w-5 h-5 text-blue-500" />
              Maximum Rain Chance
            </label>
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-600">Dry day</span>
              <span className="text-sm font-semibold text-blue-600">{preferences.maxPrecipChance}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="40"
              step="5"
              value={preferences.maxPrecipChance}
              onChange={(e) => updatePref('maxPrecipChance', parseInt(e.target.value))}
              className="w-full h-2 bg-gradient-to-r from-yellow-200 to-blue-400 rounded-lg appearance-none cursor-pointer slider"
            />
          </div>

          {/* Ocean Preferences */}
          <div className="border-t-2 border-gray-100 pt-6">
            <label className="flex items-center gap-2 text-gray-700 font-semibold mb-4">
              <Waves className="w-5 h-5 text-blue-500" />
              Ocean Conditions
            </label>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-2">Wave Preference</label>
                <div className="flex gap-3">
                  {[
                    { value: 'calm', label: 'Calm', desc: '0-2 ft' },
                    { value: 'moderate', label: 'Moderate', desc: '2-4 ft' },
                    { value: 'active', label: 'Active', desc: '4-6 ft' }
                  ].map((wave) => (
                    <button
                      key={wave.value}
                      onClick={() => updatePref('wavePreference', wave.value)}
                      className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
                        preferences.wavePreference === wave.value
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <div>{wave.label}</div>
                      <div className="text-xs opacity-70">{wave.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-2">Minimum Water Temperature</label>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Perfect for swimming</span>
                  <span className="text-sm font-semibold text-blue-600">{preferences.minWaterTemp}°F</span>
                </div>
                <input
                  type="range"
                  min="60"
                  max="85"
                  value={preferences.minWaterTemp}
                  onChange={(e) => updatePref('minWaterTemp', parseInt(e.target.value))}
                  className="w-full h-2 bg-gradient-to-r from-blue-300 to-cyan-400 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>
            </div>
          </div>

          {/* Alert Settings */}
          <div className="border-t-2 border-gray-100 pt-6">
            <label className="flex items-center gap-2 text-gray-700 font-semibold mb-4">
              <Bell className="w-5 h-5 text-cyan-500" />
              Alert Settings
            </label>

            <div className="space-y-4">
              {/* Alert Threshold */}
              <div>
                <label className="block text-sm text-gray-600 mb-2">
                  Alert me when beach score is {preferences.alertThreshold}+ out of 10
                </label>
                <input
                  type="range"
                  min="5"
                  max="10"
                  step="0.5"
                  value={preferences.alertThreshold}
                  onChange={(e) => updatePref('alertThreshold', parseFloat(e.target.value))}
                  className="w-full h-2 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>5 (Good)</span>
                  <span>7 (Great)</span>
                  <span>10 (Perfect)</span>
                </div>
              </div>

              {/* Time of Day Preference */}
              <div>
                <label className="block text-sm text-gray-600 mb-2">What time of day? <Crown className="w-3 h-3 text-amber-500 inline" /></label>
                <div className="flex gap-3">
                  {[
                    { value: 'morning', label: '🌅 Morning', sub: '8am-12pm' },
                    { value: 'afternoon', label: '☀️ Afternoon', sub: '1pm-5pm' },
                    { value: 'both', label: '🏖️ Both', sub: 'All day' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => updatePref('timeOfDayPreference', option.value)}
                      disabled={!preferences.isPremium}
                      className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
                        preferences.timeOfDayPreference === option.value
                          ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      } ${!preferences.isPremium ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div>{option.label}</div>
                      <div className="text-xs opacity-75">{option.sub}</div>
                    </button>
                  ))}
                </div>
                {!preferences.isPremium && (
                  <p className="text-xs text-amber-600 mt-2">Premium feature</p>
                )}
              </div>

              {/* Lead Time */}
              <div>
                <label className="block text-sm text-gray-600 mb-2">Alert me how far in advance?</label>
                <div className="flex gap-3">
                  {[1, 2, 3].map((days) => (
                    <button
                      key={days}
                      onClick={() => updatePref('alertLeadTime', days)}
                      className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
                        preferences.alertLeadTime === days
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {days} day{days > 1 ? 's' : ''}
                    </button>
                  ))}
                </div>
                {preferences.alertLeadTime > 1 && (
                  <p className="text-xs text-gray-500 mt-2">
                    Premium feature - requires subscription
                  </p>
                )}
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.emailEnabled}
                  onChange={(e) => updatePref('emailEnabled', e.target.checked)}
                  className="w-5 h-5 text-cyan-500 rounded focus:ring-cyan-400"
                />
                <span className="text-gray-700">Send me email alerts for perfect beach days</span>
              </label>

              {/* SMS Alerts - Optional Feature */}
              <div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 text-xs text-gray-700">
                  <strong>✅ SMS is Optional:</strong> You can use all features without SMS. Skip this to continue using the service without text notifications.
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.smsEnabled}
                    onChange={(e) => updatePref('smsEnabled', e.target.checked)}
                    className="w-5 h-5 text-cyan-500 rounded focus:ring-cyan-400"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Smartphone className="w-4 h-4" />
                      <span className="text-gray-700 font-semibold">Enable SMS Text Alerts (Optional)</span>
                      {!preferences.isPremium && <Crown className="w-4 h-4 text-amber-500" />}
                    </div>
                    <p className="text-xs text-gray-600 mt-1 ml-6">
                      By checking this box, you consent to receive automated SMS alerts about beach weather conditions. Message frequency varies based on weather conditions (typically 0-7 messages/week). Message & data rates may apply. Reply STOP to unsubscribe at any time.
                      {!preferences.isPremium && <span className="block mt-1 text-amber-600 font-semibold">Note: SMS delivery requires Premium, but you can opt-in now and upgrade later.</span>}
                    </p>
                  </div>
                </label>
                {preferences.smsEnabled && (
                  <div className="mt-2 ml-8">
                    {!preferences.isPremium && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-xs text-gray-700">
                        <strong>⚠️ Premium Required for SMS Delivery:</strong> You can enter your phone number now, but SMS alerts will only be sent once you upgrade to Premium. All other features work without Premium.
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600 font-semibold">+1</span>
                      <input
                        type="tel"
                        placeholder="(555) 123-4567 - optional"
                        value={preferences.phoneNumber?.replace(/^\+1/, '')}
                        onChange={(e) => {
                          // Only allow numbers, spaces, parentheses, and dashes
                          const cleaned = e.target.value.replace(/[^\d\s()-]/g, '');
                          updatePref('phoneNumber', cleaned);
                        }}
                        className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-cyan-400 focus:outline-none transition-colors"
                        maxLength="14"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">US phone numbers only (optional field)</p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2 text-xs text-gray-700">
                      <p className="mb-2">
                        <strong>SMS Consent:</strong> By providing your phone number and enabling SMS, you agree to receive beach weather alerts via text message.
                      </p>
                      <p className="text-xs">
                        You can opt-out anytime by replying STOP. See our{' '}
                        <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="text-cyan-600 underline">Terms</a>
                        {' '}and{' '}
                        <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="text-cyan-600 underline">Privacy Policy</a>.
                      </p>
                    </div>
                  </div>
                )}
                {!preferences.isPremium && preferences.smsEnabled && (
                  <p className="text-xs text-amber-600 ml-8 mt-1">Premium feature</p>
                )}
              </div>
            </div>
          </div>

          {/* Additional Locations - Premium */}
          {preferences.isPremium && (
            <div className="border-t-2 border-gray-100 pt-6">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-4">
                <MapPin className="w-5 h-5 text-cyan-500" />
                Additional Locations
                <Crown className="w-4 h-4 text-amber-500" />
              </label>

              <div className="space-y-3">
                {preferences.additionalLocations.map((location, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="flex-1">
                      <LocationPicker
                        value={location}
                        onChange={(newLocation) => {
                          const newLocations = [...preferences.additionalLocations];
                          newLocations[index] = newLocation;
                          updatePref('additionalLocations', newLocations);
                        }}
                        placeholder={`Location ${index + 2} (ZIP or name)`}
                      />
                    </div>
                    <button
                      onClick={() => {
                        const newLocations = preferences.additionalLocations.filter((_, i) => i !== index);
                        updatePref('additionalLocations', newLocations);
                      }}
                      className="p-3 text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}

                {preferences.additionalLocations.length < 2 && (
                  <button
                    onClick={() => updatePref('additionalLocations', [...preferences.additionalLocations, null])}
                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-cyan-400 hover:text-cyan-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Add Location ({preferences.additionalLocations.length}/2)
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Scoring Priorities - Premium */}
          {preferences.isPremium && (
            <div className="border-t-2 border-gray-100 pt-6">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2">
                <Sun className="w-5 h-5 text-cyan-500" />
                What Matters Most for Your Perfect Beach Day?
                <Crown className="w-4 h-4 text-amber-500" />
              </label>
              <p className="text-sm text-gray-600 mb-4">
                Rank your top 3 priorities - we'll weight your scores accordingly
              </p>

              <div className="space-y-3">
                {[
                  { key: 'airTemp', label: 'Air Temperature', icon: '🌡️' },
                  { key: 'waterTemp', label: 'Water Temperature', icon: '💧' },
                  { key: 'waves', label: 'Wave Conditions', icon: '🌊' },
                  { key: 'wind', label: 'Wind Speed', icon: '💨' },
                  { key: 'rain', label: 'No Rain', icon: '☔' },
                  { key: 'clouds', label: 'Clear Skies', icon: '☀️' }
                ].map((priority) => {
                  const priorityIndex = preferences.scoringPriorities?.indexOf(priority.key);
                  const isSelected = priorityIndex >= 0;
                  const rank = isSelected ? priorityIndex + 1 : null;

                  return (
                    <button
                      key={priority.key}
                      onClick={() => {
                        const current = preferences.scoringPriorities || [];
                        if (isSelected) {
                          // Remove if already selected
                          updatePref('scoringPriorities', current.filter(p => p !== priority.key));
                        } else if (current.length < 3) {
                          // Add if under 3 priorities
                          updatePref('scoringPriorities', [...current, priority.key]);
                        }
                      }}
                      className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                        isSelected
                          ? rank === 1
                            ? 'bg-gradient-to-r from-amber-100 to-yellow-100 border-amber-400 shadow-md'
                            : rank === 2
                            ? 'bg-gradient-to-r from-blue-100 to-cyan-100 border-blue-400 shadow-md'
                            : 'bg-gradient-to-r from-green-100 to-emerald-100 border-green-400 shadow-md'
                          : 'bg-white border-gray-200 hover:border-cyan-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{priority.icon}</span>
                        <span className="font-semibold text-gray-800">{priority.label}</span>
                      </div>
                      {isSelected && (
                        <div className={`px-3 py-1 rounded-full font-bold ${
                          rank === 1
                            ? 'bg-amber-500 text-white'
                            : rank === 2
                            ? 'bg-blue-500 text-white'
                            : 'bg-green-500 text-white'
                        }`}>
                          #{rank}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {preferences.scoringPriorities && preferences.scoringPriorities.length > 0 && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-gray-700">
                  <strong>Your custom scoring:</strong>
                  <ul className="mt-2 space-y-1 text-xs">
                    {preferences.scoringPriorities.map((p, i) => {
                      const priority = [
                        { key: 'airTemp', label: 'Air Temperature' },
                        { key: 'waterTemp', label: 'Water Temperature' },
                        { key: 'waves', label: 'Wave Conditions' },
                        { key: 'wind', label: 'Wind Speed' },
                        { key: 'rain', label: 'No Rain' },
                        { key: 'clouds', label: 'Clear Skies' }
                      ].find(pr => pr.key === p);
                      const weights = [40, 35, 25];
                      return (
                        <li key={i}>
                          #{i + 1}: <strong>{priority?.label}</strong> - {weights[i]}% of your score
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving || !preferences.homeLocation}
            className={`w-full py-4 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 ${
              saved
                ? 'bg-green-500'
                : 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 shadow-lg hover:shadow-xl'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {saving ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : saved ? (
              <>
                Saved! ✓
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save Preferences
              </>
            )}
          </button>

          {!preferences.homeLocation && (
            <p className="text-sm text-center text-gray-500">
              Please select your home location to save preferences
            </p>
          )}

          {/* Premium Subscription Management */}
          {preferences.isPremium && (
            <div className="mt-8 border-t-2 border-gray-100 pt-6">
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-6 border border-amber-200">
                <div className="flex items-start gap-3 mb-4">
                  <Crown className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-1">
                      Premium {preferences.premiumType === 'trial' ? 'Trial' : preferences.premiumType === 'vip' ? 'VIP' : 'Active'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {preferences.premiumType === 'trial'
                        ? 'You\'re on a 7-day free trial. Enjoy all premium features!'
                        : preferences.premiumType === 'vip'
                        ? 'You have VIP access to all premium features!'
                        : 'Thank you for being a premium member!'}
                    </p>
                  </div>
                </div>

                {/* Only show Manage Subscription button for paid/trial users, not VIP */}
                {preferences.premiumType !== 'vip' && (
                  <>
                    <button
                      onClick={handleManageSubscription}
                      className="w-full py-3 px-4 bg-white border-2 border-gray-300 hover:border-cyan-500 text-gray-700 hover:text-cyan-600 rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
                    >
                      Manage Subscription
                    </button>

                    <p className="text-xs text-gray-500 text-center mt-3">
                      Cancel anytime, update payment method, or view billing history
                    </p>
                  </>
                )}

                {/* Show different message for VIP users */}
                {preferences.premiumType === 'vip' && (
                  <p className="text-sm text-gray-600 text-center">
                    Questions about your VIP access? Contact support@10outtaten.com
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Premium Upgrade CTA */}
          {!preferences.isPremium && (
            <div className="mt-8">
              <PremiumUpgrade onUpgrade={handleUpgrade} />
            </div>
          )}

          {/* Delete Account Section */}
          <div className="mt-8 pt-8 border-t-2 border-red-100">
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-red-800 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Danger Zone
              </h3>
              <p className="text-sm text-red-700 mb-4">
                Once you delete your account, there is no going back. This will permanently delete:
              </p>
              <ul className="text-sm text-red-700 space-y-1 mb-4 ml-4 list-disc">
                <li>Your profile and preferences</li>
                <li>All your locations and settings</li>
                <li>Your feedback history</li>
                <li>Your referral code and count</li>
                <li>Your subscription (if you have one)</li>
              </ul>

              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors"
                >
                  Delete My Account
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="bg-white border-2 border-red-300 rounded-lg p-4">
                    <p className="font-bold text-red-900 mb-2">⚠️ Are you absolutely sure?</p>
                    <p className="text-sm text-red-800">
                      This action <strong>CANNOT</strong> be undone. This will permanently delete your account and remove all your data from our servers.
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleDeleteAccount}
                      disabled={deleting}
                      className="flex-1 px-6 py-3 bg-red-700 text-white rounded-lg font-bold hover:bg-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deleting ? 'Deleting...' : 'Yes, Delete Forever'}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={deleting}
                      className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
